import { createAction } from 'Utility/Redux';
import Point from '../../Utility/Point';
import { IPlatformState } from './IPlatformState';
import { LevelStatus } from '../Level/LevelStatus';
import { RewardType, IAPReward } from "Utility/RewardHelper";
import {
    ContextType,
    ResponseType,
    ResponseStatus,
    IContextRequest,
} from './ContextType';
import { IGameState } from './IGameState';
import { IEntryPoint } from './IEntryPoint';
import { CrossPromoStatus, IUserState } from './IUserState';
import { IPlayerState } from '../Player/IPlayerState';
import { MyPetMiniGameSceneParams } from "Scene/MyPetMiniGameScene";
import { PreBoosterType } from '../Card/BoosterType';
import { ISceneParams } from 'Scene/Scene';
import { IPerSessionData } from 'State/Game/IPerSessionData';
import { IEventsState } from 'State/Events/IEventsState';
import { FarmItemId } from "System/Farm/Data/FarmItemData";

export enum Types {
    SET_STATE = 'setState',
    SET_PER_SESSION_DATA = 'setPerSessionData',
    ENABLE_IAP = 'enableIAP',
    SET_SCENE_LOADED = 'setSceneLoaded',
    SET_SCENE_LOADING = 'setSceneLoading',
    GAME_LOADED = 'gameLoaded',
    LOAD_WORLD = 'loadWorld',
    LOAD_LEVEL = 'loadLevel',
    LOAD_LUCKY_TURNIPS = 'loadLuckyTurnips',
    LOAD_MY_PET_MINI_GAME = 'loadMyPetMiniGame',
    LOAD_FARM_SCENE = 'loadFarmScene',
    RESTART_LEVEL = 'restartLevel',
    SET_CONTEXT = 'setContext',
    CLEAR_CONTEXT = 'clearContext',
    RESTORE_DEFAULT_CONTEXT = 'restoreDefaultContext',
    SET_CANVAS_SIZE = 'setCanvasSize',
    END_LEVEL = 'endLevel',
    SET_LOADED_DATA = 'setLoadedData',
    FARM_UNLOCK_NEW_ITEMS = 'farmUnlockNewItems',
    SET_CROSS_PROMO = 'setCrossPromo',
}

export enum GiftType {
    coins = 'coins',
    lives = 'lives',
    joker = 'joker',
    TNT = 'TNT',
    Shuffle = 'Shuffle',
}

export interface IBotMessageGift {
    giftData: IGiftData[];
    id: string;
}

export interface IGiftData {
    amount: number;
    itemId: RewardType;
}

export interface IGameLoadData {
    userState: Partial<IUserState>;
    platform: Partial<IPlatformState>;
    playerState: IPlayerState;
    entryPoint: IEntryPoint;
    eventsState: IEventsState;
}

export interface IEndLevelData {
    status:
        | LevelStatus.RESTARTING
        | LevelStatus.FAILED
        | LevelStatus.COMPLETE
        | LevelStatus.EXITING;
    firstComplete: boolean;
}

export interface ILoadWorldMetaData {
    worldNumber?: number;
    levelComplete?: boolean;

    /**
     * Temporary fix for UI that needs to activate after the world loads.
     */
    clearUI?: boolean;
    firstComplete?: boolean;
    firstLoad?: boolean;
    starsGained?: number;
    petSpawnLevel?: number;
}

export const GameActions = {
    setState: createAction(Types.SET_STATE, resolve => (state: IGameState) =>
        resolve<IGameState>(state)
    ),
    setPerSessionData: createAction(Types.SET_PER_SESSION_DATA, resolve => (state: Partial<IPerSessionData>) =>
        resolve(state),
    ),
    gameLoaded: createAction(Types.GAME_LOADED),
    setLoadedData: createAction(
        Types.SET_LOADED_DATA,
        resolve => (data: IGameLoadData) => resolve<IGameLoadData>(data)
    ),
    loadWorld: createAction(
        Types.LOAD_WORLD,
        resolve => (params?: ILoadWorldMetaData) =>
            resolve<ILoadWorldMetaData | undefined>(params)
    ),

    loadLevel: createAction(
        Types.LOAD_LEVEL,
        resolve => (payload: {
            levelNumber: number;
            preBoosters: PreBoosterType[];
            restart?: boolean;
            isLevelTest?: boolean;
        }) => resolve(payload),
    ),

    loadLuckyTurnips: createAction(Types.LOAD_LUCKY_TURNIPS, resolve => () =>
        resolve()
    ),

    loadMyPetMiniGame: createAction(
        Types.LOAD_MY_PET_MINI_GAME,
        resolve => (params: MyPetMiniGameSceneParams) => resolve(params)
    ),

    loadFarmScene: createAction(
        Types.LOAD_FARM_SCENE,
        resolve => (params: ISceneParams) => resolve(params)
    ),

    endLevel: createAction(Types.END_LEVEL, resolve => (data: IEndLevelData) =>
        resolve(data)
    ),

    restartLevel: createAction(
        Types.RESTART_LEVEL,
        resolve => (levelNumber: number) => resolve(levelNumber)
    ),

    setContext: createAction(
        Types.SET_CONTEXT,
        resolve => (context: ContextType, data?: {}) =>
            resolve<IContextRequest>({ context: context, data: data })
    ),

    clearContextData: createAction(Types.CLEAR_CONTEXT, resolve => () =>
        resolve()
    ),

    restoreContext: createAction(
        Types.RESTORE_DEFAULT_CONTEXT,
        resolve => (status: ResponseStatus, type: ResponseType, data?: {}) =>
            resolve<{
                status: ResponseStatus;
                type: ResponseType;
                data: {};
            }>({
                status: status,
                type: type,
                data: data,
            })
    ),

    setCanvasSize: createAction(
        Types.SET_CANVAS_SIZE,
        resolve => (size: Point) => resolve<Point>(size)
    ),
    setSceneLoading: createAction(Types.SET_SCENE_LOADING),
    setSceneLoaded: createAction(Types.SET_SCENE_LOADED),

    enableIAP: createAction(
        Types.ENABLE_IAP,
        resolve => (unconsumedPurchases: IAPReward[]) =>
            resolve<IAPReward[]>(unconsumedPurchases)
    ),

    setCrossPromo: createAction(
        Types.SET_CROSS_PROMO,
        resolve => (crossPromoResult: {playerState: IPlayerState, crossPromoStatus: CrossPromoStatus}) => resolve(crossPromoResult)
    ),
};
